/*
 * Copyright 2026 North Carolina State University
 */

/**
 * Frontend Invoice Calculations
 * 
 * ⚠️ MIGRATION NOTICE: 
 * Most calculation logic has been moved to the backend unified billing system.
 * Frontend should use API calls instead of local calculations.
 * 
 * This file contains only:
 * 1. New unified API call function
 * 2. Basic utility functions (calculateDays)
 * 3. Deprecated compatibility functions
 */

import { priceOn, priceChamberRow } from './ratePricing';

/**
 * NEW UNIFIED SYSTEM: Calculate invoice details using backend unified billing system
 * @param {string} projectId - Project ID
 * @param {string} billStartDate - Billing period start (YYYY-MM-DD)
 * @param {string} billEndDate - Billing period end (YYYY-MM-DD)
 * @returns {Promise<Object>} Complete invoice calculation from backend
 */
export const calculateInvoiceDetailsUnified = async (projectId, billStartDate, billEndDate) => {
  try {
    // Import axios dynamically to avoid circular imports
    const axios = await import('./axios');
    
    const response = await axios.default.post('/api/invoices/calculate', {
      project_id: projectId,
      bill_start_date: billStartDate,
      bill_end_date: billEndDate
    });
    
    return response.data;
  } catch (error) {
    console.error('Error calculating invoice details:', error);
    throw error;
  }
};

// Helper function to parse a date from database as a local date (avoiding timezone shift)
// When a date is stored as "2025-01-01T00:00:00.000Z", this ensures we get Jan 1 locally
const parseAsLocalDate = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) {
    // If already a Date, extract UTC parts and create local date
    return new Date(dateValue.getUTCFullYear(), dateValue.getUTCMonth(), dateValue.getUTCDate(), 12, 0, 0, 0);
  }
  // If string, parse and create local date from UTC parts
  const d = new Date(dateValue);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0, 0);
};

/**
 * Extract the intended calendar day (year, month, day) from a date value.
 *
 * - For strings ("YYYY-MM-DD" or full ISO), the literal date part is used so we
 *   never re-interpret the day through a timezone.
 * - For Date objects (which in this app are always constructed in local time,
 *   e.g. local noon), the LOCAL calendar parts are used.
 *
 * @returns {[number, number, number] | [null, null, null]} [year, monthIndex, day]
 */
const extractCalendarYMD = (value) => {
  if (value == null) return [null, null, null];
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return [null, null, null];
    return [value.getFullYear(), value.getMonth(), value.getDate()];
  }
  const str = String(value);
  const datePart = str.split('T')[0];
  const parts = datePart.split('-');
  if (parts.length === 3) {
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) return [y, m, d];
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return [null, null, null];
  return [parsed.getFullYear(), parsed.getMonth(), parsed.getDate()];
};

/**
 * Calculate the number of days between two dates, INCLUSIVE of both the start
 * and end date. This is the SINGLE source of truth for day counts on the
 * frontend and mirrors the backend `calculateDays` in billingUtils.js.
 *
 * Both dates are anchored to 12:00 UTC on their intended calendar day, so the
 * result is immune to Daylight Saving Time transitions. Previously, comparing
 * local-noon dates across a DST boundary (e.g. Jan -> Jun) left the elapsed
 * time one hour short of a whole number of days, and Math.floor dropped a full
 * day (e.g. 1/1 -> 6/30 returned 180 instead of the correct 181).
 */
export const calculateDays = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  const [sy, sm, sd] = extractCalendarYMD(startDate);
  const [ey, em, ed] = extractCalendarYMD(endDate);
  if (sy == null || ey == null) return 0;

  const startUTC = Date.UTC(sy, sm, sd, 12, 0, 0, 0);
  const endUTC = Date.UTC(ey, em, ed, 12, 0, 0, 0);

  // Both anchored at 12:00 UTC -> the difference is always a whole number of days.
  const dayDiff = Math.round((endUTC - startUTC) / (1000 * 60 * 60 * 24));
  return Math.max(0, dayDiff + 1);
};

/**
 * An assignment is treated as "active" (still ongoing and therefore billable up
 * to the bill end date) when its status is Active or missing. Completed and
 * Terminated assignments have a fixed end date that must not be extended.
 */
export const isAssignmentActive = (status) => !status || status === 'Active';

/**
 * A sentinel far-future date is used by the backend to represent an ongoing
 * assignment with no end date. Treat any date in the year 2100+ as "no real
 * end date" so it never clamps a real bill end date.
 */
const isSentinelEndDate = (value) => {
  if (!value) return true;
  const [year] = extractCalendarYMD(value);
  return year == null || year >= 2100;
};

/**
 * Clamp a requested bill end date so a chamber row is never billed beyond the
 * assignment's own end date.
 *
 * This is what prevents a COMPLETED assignment (e.g. ended Jan 31) from having
 * its billing end date pushed out to a later bill end date (e.g. Jun 30). For
 * active/ongoing assignments (no real end date) the requested date is returned
 * unchanged.
 *
 * @param {Date} requestedEndDate - The new bill end date (local Date object)
 * @param {*} assignmentEndDate - The assignment's actual end date (Date/string)
 * @returns {Date} The clamped end date as a local-noon Date object
 */
export const clampEndDateToAssignment = (requestedEndDate, assignmentEndDate) => {
  const requested = requestedEndDate instanceof Date
    ? new Date(requestedEndDate)
    : (() => { const [y, m, d] = extractCalendarYMD(requestedEndDate); return y == null ? null : new Date(y, m, d, 12, 0, 0, 0); })();
  if (!requested) return requestedEndDate;

  if (isSentinelEndDate(assignmentEndDate)) return requested;

  const [ay, am, ad] = extractCalendarYMD(assignmentEndDate);
  if (ay == null) return requested;
  const assignmentEnd = new Date(ay, am, ad, 12, 0, 0, 0);

  // Bill only through the assignment's own end date when it is earlier.
  return assignmentEnd < requested ? assignmentEnd : requested;
};

/**
 * ❌ DEPRECATED: Use backend unified billing system instead
 * 
 * Frontend should call calculateInvoiceDetailsUnified() or /api/invoices/calculate endpoint.
 * This function exists only for backward compatibility and will be removed.
 */
export const calculateChamberCost = (project, billStartDate, billEndDate, unbilledPeriodsData = null) => {
  // For backward compatibility, return basic structure
  if (!project || !billStartDate) {
    return { unitCosts: [], totalCost: 0 };
  }

  // If unbilled periods data is provided (from backend), use it
  if (unbilledPeriodsData && Array.isArray(unbilledPeriodsData.unbilled_periods)) {
    // Convert backend format to frontend format for compatibility
    // Merge CONSECUTIVE periods but keep DISCRETE (non-consecutive) periods separate
    const unitCosts = [];
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    // Helper to check if two periods are consecutive (end of first + 1 day = start of second)
    const areConsecutive = (end1, start2) => {
      const endDate = new Date(end1);
      endDate.setHours(0, 0, 0, 0);
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);
      
      const startDate = new Date(start2);
      startDate.setHours(0, 0, 0, 0);
      
      return nextDay.getTime() === startDate.getTime();
    };
    
    unbilledPeriodsData.unbilled_periods.forEach((item, itemIndex) => {
      const rate = item.rate || 0;
      const quantity = item.quantity || 0;
      
      if (item.unbilled_periods && Array.isArray(item.unbilled_periods) && item.unbilled_periods.length > 0) {
        // Sort periods by start date - use parseAsLocalDate to avoid timezone shift
        const sortedPeriods = [...item.unbilled_periods].sort((a, b) => 
          parseAsLocalDate(a.start_date) - parseAsLocalDate(b.start_date)
        );
        
        // Merge consecutive periods, keep discrete periods separate
        const mergedPeriods = [];
        let currentMerged = null;
        
        sortedPeriods.forEach((period) => {
          // Use parseAsLocalDate to avoid timezone shift from UTC dates
          let startDate = parseAsLocalDate(period.start_date);
          let endDate = parseAsLocalDate(period.end_date);
          
          // Cap end date to today if it's in the future
          if (endDate > today) {
            endDate = new Date(today);
          }
          
          // Skip periods that are entirely in the future
          if (startDate > today) {
            return;
          }
          
          if (!currentMerged) {
            // Start a new merged period
            currentMerged = { startDate, endDate };
          } else if (areConsecutive(currentMerged.endDate, startDate)) {
            // Periods are consecutive - extend the current merged period
            currentMerged.endDate = endDate;
          } else {
            // Periods are NOT consecutive - save current and start new
            mergedPeriods.push(currentMerged);
            currentMerged = { startDate, endDate };
          }
        });
        
        // Don't forget the last merged period
        if (currentMerged) {
          mergedPeriods.push(currentMerged);
        }
        
        // Create entries for each merged period. A row can span a price change;
        // its cost prices each day from the rate's schedule, and it is split
        // into one invoice line per price when the invoice is generated.
        mergedPeriods.forEach((mergedPeriod, periodIndex) => {
          const daysUsed = calculateDays(mergedPeriod.startDate, mergedPeriod.endDate);
          const priced = priceChamberRow(
            { rate, quantity, priceSchedule: item.price_schedule, rateBasis: item.rate_basis },
            mergedPeriod.startDate,
            mergedPeriod.endDate
          );
          const totalCost = priced.totalCost;
          
          unitCosts.push({
            uniqueKey: `${item.chamber_id}_${item._assignment_id}_${itemIndex}_${periodIndex}`,
            chamberId: item.chamber_id,
            chamberName: item.chamber_name || 'Unknown Compartment',
            assignmentId: item._assignment_id,
            unitId: item.unit_id,
            unitName: item.unit_name || 'Unknown Unit',
            rateName: item.rate_name || item.rate_id || 'Unknown Rate',
            rateBasis: item.rate_basis || 'day',
            rateId: item.rate_id,
            quantity: quantity,
            rate: priced.rate,
            priceSchedule: item.price_schedule,
            priceSegments: priced.segments,
            startDate: mergedPeriod.startDate,
            endDate: mergedPeriod.endDate,
            daysUsed: daysUsed,
            billedUnits: priced.units,
            totalCost: totalCost,
            // Assignment status + actual end date so callers can distinguish
            // Active vs Completed assignments and avoid extending a completed
            // assignment's billing end date past its real end date.
            assignmentStatus: item.assignment_status,
            assignmentEndDate: item.assignment_end_date
          });
        });
      }
    });
    
    const totalCost = unitCosts.reduce((sum, cost) => sum + (cost.totalCost || 0), 0);
    return { unitCosts, totalCost };
  }

  // Fallback: Basic calculation for backward compatibility (should not be used)
  console.error('❌ Using deprecated fallback calculation - should use backend API instead');
  return { unitCosts: [], totalCost: 0 };
};

/**
 * ❌ DEPRECATED: Use backend unified billing system instead
 * 
 * Resources are now calculated in the backend unified system.
 */
export const calculateResourceCost = (project, priceDate = null) => {
  if (!project?.resource_assignments || !Array.isArray(project.resource_assignments)) {
    return { resources: [], totalCost: 0 };
  }

  const resources = project.resource_assignments.map((assignment, index) => {
    const unit = assignment.resource?.unit;
    const rateId = assignment.rate_id;

    // Resources are one-off quantities, priced at the rate in effect on the
    // billing period end date (same rule as the backend's getResourcePrice).
    let rate = 0;
    let rateName = '';
    if (unit?.rates && Array.isArray(unit.rates) && rateId) {
      const rateObj = unit.rates.find(r => r.rate_id === rateId);
      if (rateObj) {
        rate = priceOn(rateObj, priceDate);
        rateName = rateObj.display_name || rateId;
      }
    }
    if (rate === 0 && unit?.rates?.length > 0 && !rateId) {
      rate = priceOn(unit.rates[0], priceDate);
      rateName = unit.rates[0].display_name || '';
    }

    const invoicedQty = (assignment.invoiced_quantities || []).reduce(
      (sum, iq) => sum + (iq.quantity || 0), 0
    );
    const unbilledQty = Math.max(0, (assignment.quantity || 0) - invoicedQty);

    const rateObj = unit?.rates?.find(r => r.rate_id === rateId);

    return {
      uniqueKey: `resource_${assignment._id || assignment.resource?._id || index}_${index}`,
      assignmentId: assignment._id,
      resourceId: assignment.resource?._id,
      resourceName: assignment.resource?.resource || 'Unknown Resource',
      unitName: unit?.base_unit || unit?.unit_short || 'unit',
      rateId: rateId || '',
      rateName,
      rateBasis: rateObj?.rate_basis || 'unit',
      quantity: unbilledQty,
      totalQuantity: assignment.quantity || 0,
      invoicedQuantity: invoicedQty,
      rate,
      totalCost: unbilledQty * rate
    };
  }).filter(r => r.quantity > 0);

  const totalCost = resources.reduce((sum, resource) => sum + (resource.totalCost || 0), 0);
  return { resources, totalCost };
};

/**
 * ❌ DEPRECATED: Use backend unified billing system instead
 * 
 * Total calculations should be done in the backend.
 */
export const calculateTotalBillAmount = (project, billStartDate, billEndDate, adjustments = []) => {
  console.warn('❌ DEPRECATED: calculateTotalBillAmount should use calculateInvoiceDetailsUnified() instead');
  
  const { totalCost: chamberTotal } = calculateChamberCost(project, billStartDate, billEndDate);
  const { totalCost: resourceTotal } = calculateResourceCost(project);
  const adjustmentTotal = (adjustments || []).reduce((sum, adj) => sum + (adj.amount || 0), 0);
  
  return chamberTotal + resourceTotal + adjustmentTotal;
};